/**
 * OAuth Module
 *
 * @module oauth
 * @category OAuth
 *
 * Handles OAuth account binding management:
 * - List connected OAuth accounts
 * - Bind new OAuth account
 * - Unbind OAuth account
 */

import type { HttpClient, ClientResult } from "@amaster.ai/http-client";
import type { OAuthBinding, OAuthProvider, SuccessResponse } from "../types";

export interface OAuthModuleDeps {
  http: HttpClient;
  storage: { getItem: (key: string) => string | null };
}

export function createOAuthModule(deps: OAuthModuleDeps) {
  const { http, storage } = deps;

  return {
    /**
     * Get list of OAuth accounts bound to current user
     *
     * @category OAuth
     * @example
     * ```typescript
     * const result = await oauth.getOAuthBindings();
     * if (result.data) {
     *   result.data.forEach(binding => {
     *     console.log(`${binding.provider}: ${binding.email}`);
     *   });
     * }
     * ```
     */
    async getOAuthBindings(): Promise<ClientResult<OAuthBinding[]>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<OAuthBinding[]>({
        url: "/api/auth/oauth-bindings",
        method: "get",
        headers: { Authorization: `Bearer ${token}` },
      });
    },

    /**
     * Bind an OAuth account to current user
     *
     * @category OAuth
     * @example
     * ```typescript
     * oauth.bindOAuth("google"); // Redirects to Google OAuth
     * ```
     */
    bindOAuth(provider: OAuthProvider): void {
      if (typeof window === "undefined") {
        console.error("[AuthClient] OAuth binding is only available in browser environment");
        return;
      }

      window.location.href = `/api/auth/oauth/${provider}/bind`;
    },

    /**
     * Unbind an OAuth account from current user
     *
     * @category OAuth
     * @example
     * ```typescript
     * await oauth.unbindOAuth("google");
     * ```
     */
    async unbindOAuth(provider: OAuthProvider): Promise<ClientResult<SuccessResponse>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<SuccessResponse>({
        url: `/api/auth/oauth/${provider}/unbind`,
        method: "delete",
        headers: { Authorization: `Bearer ${token}` },
      });
    },
  };
}

export type OAuthModule = ReturnType<typeof createOAuthModule>;
